import { Component, computed, ElementRef, input, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { IS_PROD_BUILD } from '@examdojo/core/environment';
import { ReactWrapperComponent } from '@examdojo/core/react-wrapper';
import { SupabaseService } from '@examdojo/core/supabase';
import { fromProviderEvent, TiptapEditorFormControlDirective, TiptapEditorOutputFormat } from '@examdojo/core/tiptap';
import { NotionEditor } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-templates/notion-like/notion-like-editor';
import type {
  SlashMenuConfig,
  SlashMenuItem,
} from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/slash-dropdown-menu/use-slash-dropdown-menu';
import type { TiptapSmartLinkConfig } from '@examdojo/core/tiptap-react-ui-components/contexts/smart-link-context';
import type { CollabConfig } from '@examdojo/core/tiptap-react-ui-components/lib/collab-config';
import { ErrorHandlerService } from '@examdojo/error-handling';
import { TableOfContentData } from '@tiptap-pro/extension-table-of-contents';
import type {
  onAuthenticationFailedParameters,
  onDisconnectParameters,
  TiptapCollabProvider,
} from '@tiptap-pro/provider';
import type { Editor, HTMLContent } from '@tiptap/core';
import { JSONContent } from '@tiptap/react';
import { createElement, ReactElement } from 'react';
import { EMPTY, merge, switchMap, tap } from 'rxjs';
import type { Doc as YDoc } from 'yjs';

@Component({
  selector: 'dojo-tiptap-editor-wrapper',
  standalone: true,
  imports: [TiptapEditorFormControlDirective, ReactiveFormsModule],
  template: `
    <div
      #notionEditorContainer
      class="min-h-full w-full"
      tiptap
      [editor]="editor()"
      [formControl]="valueControl()"
      [outputFormat]="outputFormat()"
    ></div>
  `,
})
export class TiptapEditorWrapperComponent<
  T extends TiptapEditorOutputFormat = TiptapEditorOutputFormat,
> extends ReactWrapperComponent {
  constructor(
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly supabaseService: SupabaseService,
  ) {
    super();
    this.listenToCollabProviderEvents().pipe(takeUntilDestroyed()).subscribe();
  }

  readonly mountElementRef = viewChild.required<ElementRef>('notionEditorContainer');
  readonly placeholder = input<string>();
  readonly editorOptions = input<Partial<Editor['options']>>();
  readonly outputFormat = input.required<T>();
  readonly valueControl =
    input.required<FormControl<T extends TiptapEditorOutputFormat.Html ? HTMLContent : JSONContent>>();
  readonly editable = input<boolean>(true);
  readonly scrollableParent = input<HTMLElement>();
  readonly slashMenuItems = input<Record<string, SlashMenuItem>>();
  readonly contentPadding = input<string>();
  readonly smartLinkConfig = input<TiptapSmartLinkConfig>();
  readonly collabConfig = input<CollabConfig>();

  readonly editor = signal<Editor | null>(null);
  readonly tableOfContentData = signal<TableOfContentData>([]);
  readonly collabProvider = signal<TiptapCollabProvider | null>(null);
  readonly collabDoc = signal<YDoc | null>(null);
  /** Emits `true` once the collab provider has completed its initial sync with Tiptap Cloud. */
  readonly collabSynced = signal(false);
  readonly collabError = signal<onAuthenticationFailedParameters | null>(null);

  readonly editor$ = toObservable(this.editor);
  readonly tableOfContentData$ = toObservable(this.tableOfContentData);

  private readonly handleEditorReady = (editor: Editor) => {
    this.editor.set(editor);
  };

  private readonly handleCollabReady = (provider: TiptapCollabProvider, ydoc: YDoc) => {
    this.collabSynced.set(provider.isSynced);
    this.collabError.set(null);
    this.collabProvider.set(provider);
    this.collabDoc.set(ydoc);
  };

  private readonly handleCollabUnavailable = () => {
    this.collabProvider.set(null);
    this.collabDoc.set(null);
    this.collabSynced.set(false);
    this.collabError.set({ reason: 'Collab token unavailable' });
  };

  private readonly handleGetAuthToken = async (): Promise<string | null> => {
    try {
      return (await this.supabaseService.getToken()) ?? null;
    } catch {
      return null;
    }
  };

  private readonly handleTableOfContentUpdate = (content: TableOfContentData) => {
    this.tableOfContentData.set(content);
  };

  readonly slashMenuConfig = computed<SlashMenuConfig | undefined>(() => {
    const items = this.slashMenuItems();
    if (!items || Object.keys(items).length === 0) {
      return undefined;
    }

    const itemsMetadata: Record<string, SlashMenuItem['metadata']> = {};
    const itemImplementations: Record<string, SlashMenuItem['implementation']> = {};

    Object.entries(items).forEach(([key, item]) => {
      itemsMetadata[key] = item.metadata;
      itemImplementations[key] = item.implementation;
    });

    return {
      itemsMetadata,
      itemImplementations,
    };
  });

  protected readonly reRenderTriggers = [
    this.placeholder,
    this.editorOptions,
    this.editable,
    this.scrollableParent,
    this.slashMenuItems,
    this.contentPadding,
    this.smartLinkConfig,
    this.collabConfig,
  ];

  protected renderReactElement(): ReactElement {
    return createElement(NotionEditor, {
      getAuthToken: this.handleGetAuthToken,
      placeholder: this.placeholder(),
      editorOptions: this.editorOptions(),
      editable: this.editable(),
      slashMenuConfig: this.slashMenuConfig(),
      contentPadding: this.contentPadding(),
      smartLinkConfig: this.smartLinkConfig(),
      collabConfig: this.collabConfig(),
      onEditorReady: this.handleEditorReady,
      onCollabReady: this.handleCollabReady,
      onCollabUnavailable: this.handleCollabUnavailable,
      tableOfContent: {
        onUpdate: this.handleTableOfContentUpdate,
        scrollableParent: this.scrollableParent(),
      },
    });
  }

  private listenToCollabProviderEvents() {
    return toObservable(this.collabProvider).pipe(
      switchMap((provider) => {
        if (!provider) {
          this.collabSynced.set(false);
          return EMPTY;
        }

        if (provider.isSynced && !this.collabSynced()) {
          this.collabSynced.set(true);
        }

        const synced$ = fromProviderEvent(provider, 'synced').pipe(
          tap(() => {
            this.collabSynced.set(true);
          }),
        );

        const authFailed$ = fromProviderEvent<onAuthenticationFailedParameters>(provider, 'authenticationFailed').pipe(
          tap((data) => {
            this.collabError.set(data);
            this.errorHandlerService.error(
              `[TiptapEditorWrapperComponent]: Tiptap Collab authentication failed. Reason: ${data.reason}`,
              {
                toast: IS_PROD_BUILD ? undefined : `Tiptap Collab authentication failed. Reason: ${data.reason}`,
              },
            );
          }),
        );

        const disconnected$ = fromProviderEvent<onDisconnectParameters>(provider, 'disconnect').pipe(
          tap(({ event }) => {
            if (this.collabSynced() || this.collabError()) {
              return;
            }

            const reason = `WebSocket disconnected (code: ${event.code})`;
            this.collabError.set({ reason });

            this.errorHandlerService.error(
              `[TiptapEditorWrapperComponent]: Tiptap Collab connection failed. ${reason}`,
              {
                toast: IS_PROD_BUILD
                  ? undefined
                  : {
                      title: `Tiptap Collab connection failed. ${reason}`,
                    },
              },
            );
          }),
        );

        return merge(synced$, authFailed$, disconnected$);
      }),
    );
  }
}
